import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { getClearHistoryAPIUrl } from '../../selectors/apiSelectors';
import locationContext from '../../context/locationsContext';
import { invokeSoftDelete } from '../../utils/clearSearchFetchRequest';
import { useRecentSearchAPI } from '../../hooks/apiHooks';
import { CLEAR_SEARCH_POSITION } from '../../constants/recentSearchConstants';
import { getAnalyticsUtils } from '../../utils/getAnalyticsUtils';
import styles from './Suggestions.scss';

const CategoryHeader = ({
  IconNode,
  headerCopy,
  categoryIndex,
  headerText,
  hasClearHistory,
}) => {
  const { formatMessage } = useIntl();
  const clearHistoryAPIUrl = useSelector(getClearHistoryAPIUrl);
  const {
    state: { isRoundTrip, focusedLocationRef },
    actions: { onCloseFullScreenInput },
    config: { lineOfBusiness },
  } = React.useContext(locationContext);
  const { reRunRecentSearchAPI } = useRecentSearchAPI();
  const onClearHistory = async () => {
    onCloseFullScreenInput();
    await invokeSoftDelete({
      clearHistoryAPIUrl,
      isRoundTrip,
      lineOfBusiness,
      reRunRecentSearchAPI,
    });
  };
  return (
    <li
      className="width-full"
      id={`category-${categoryIndex}-header`}
      role="presentation"
    >
      {categoryIndex > 0 && (
        <hr className="margin-1-tb border-t pad-1-b color-border-subtle" role="presentation" />
      )}
      <div className="flex flex-justify-between flex-align-items-center pad-2-r pad-2-l pad-0-tb">
        <div className="flex color-status-graphic-information">
          <IconNode
            role="presentation"
            color="brand"
            size="md"
            isFilled="true"
            className="pad-1-r"
          />
          <h2
            role="presentation"
            id={`category-${categoryIndex}-headerText`}
            className="color-text-brand body-1 font-weight-bold"
          >
            {headerText || <FormattedMessage id={headerCopy} />}
          </h2>
        </div>
        {hasClearHistory && (
          <Button
            variant="tertiary"
            isInline="true"
            onClick={onClearHistory}
            className={clsx(styles.suggestion, {
              'focus-light': focusedLocationRef === CLEAR_SEARCH_POSITION,
            })}
            role="option"
            aria-current={focusedLocationRef === CLEAR_SEARCH_POSITION}
            aria-selected={focusedLocationRef === CLEAR_SEARCH_POSITION}
            id={`suggestion-${CLEAR_SEARCH_POSITION}`}
            reactlytics={{
              element: getAnalyticsUtils({
                type: 'clearrecentsearches',
                lob: lineOfBusiness,
                isRoundTrip,
              }),
            }}
            tabIndex={-1}
          >
            <p className="body-1">
              {formatMessage({ id: 'recentSearches.clearHistory' })}
            </p>
          </Button>
        )}
      </div>
    </li>
  );
};

CategoryHeader.propTypes = {
  IconNode: PropTypes.func,
  iconProps: PropTypes.shape({
    className: PropTypes.string,
    filled: PropTypes.bool,
    size: PropTypes.string,
  }),
  headerCopy: PropTypes.string,
  headerText: PropTypes.string,
  categoryIndex: PropTypes.number.isRequired,
  hasClearHistory: PropTypes.bool,
};

CategoryHeader.defaultProps = {
  IconNode: null,
  headerCopy: null,
  headerText: null,
};

export default CategoryHeader;
